// Pure helpers for the bookings schedule (header pill + slide-over). London
// wall-clock everywhere: an admin reads "12:00 today", not an ISO string.

const TZ = 'Europe/London';

/** 'YYYY-MM-DD' of an instant, in London. */
export function londonDayKey(iso) {
    const d = new Date(iso);
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
}

/** 'Today' / 'Tomorrow' / 'Thu 10 Sep'. */
export function dayLabel(iso, now = Date.now()) {
    const key = londonDayKey(iso);
    if (key === londonDayKey(now)) return 'Today';
    if (key === londonDayKey(now + 86400000)) return 'Tomorrow';
    return new Date(iso).toLocaleDateString('en-GB', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' }).replace('Sept', 'Sep');
}

/** '12:00'. */
export function timeLabel(iso) {
    return new Date(iso).toLocaleTimeString('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
}

/** Rows (any order) → [{ key, label, rows }] by London day, days ascending, rows by start. */
export function groupByDay(rows, now = Date.now()) {
    const map = new Map();
    for (const r of [...(rows || [])].sort((a, b) => new Date(a.start_time) - new Date(b.start_time))) {
        const key = londonDayKey(r.start_time);
        if (!map.has(key)) map.set(key, { key, label: dayLabel(r.start_time, now), rows: [] });
        map.get(key).rows.push(r);
    }
    return [...map.values()];
}

/** Starts within the next `minutes` and has not started yet. */
export function dueSoon(iso, now = Date.now(), minutes = 60) {
    const t = new Date(iso).getTime();
    return t > now && t - now <= minutes * 60000;
}

export function bookingWho(row) {
    if (!row) return 'Unknown';
    const candidate = [row.first_name, row.last_name].filter(Boolean).join(' ');
    return row.contact_name || candidate || row.phone || 'Unknown';
}

/** 'Call back: Mikey' / 'Demo: Sam, Acme' / 'Meet & Greet: Daniel Panford at Basildon'. */
export function bookingTitle(row) {
    if (!row) return '';
    if (row.kind === 'callback') return `Call back: ${bookingWho(row)}`;
    if (row.kind === 'demo') return `Demo: ${bookingWho(row)}`;
    return `Meet & Greet: ${bookingWho(row)}${row.depot_name ? ` at ${row.depot_name}` : ''}`;
}

/** The pill: how many today, and whether one is about to start. */
export function pillState(schedule, now = Date.now()) {
    const upcoming = schedule?.upcoming || [];
    const overdue = schedule?.overdue || [];
    const todayKey = londonDayKey(now);
    const today = [...overdue, ...upcoming].filter((r) => londonDayKey(r.start_time) === todayKey).length;
    const soon = upcoming.find((r) => dueSoon(r.start_time, now));
    return { today, soon: soon || null, overdue: overdue.length, next: upcoming[0] || null };
}
